import { SchemaOrgItemProp, SchemaOrgItemType } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { ProductType } from '@headless-workspace/domain/common/client';
import {
    AsyncComponent,
    CrossSellsBundlesContainer,
    getCachedVipBrands,
    getPromotionInfosMapServerAction,
    getRecommendedProductsServerAction,
    isBrandCornerBanner,
    isCrossSellsBundlesEnabled,
    ProductDetailValue,
} from '@headless-workspace/domain/common/server';
import { PropsWithLocale } from '@headless-workspace/glow-ds';
import { ProductDetailCarouselContainer, ProductDetailContainerSection } from './../../../client';
import { ProductDetailFooterSection } from './ProductDetailFooterSection';
import { ProductDetailBrandCornerSection } from './brandCorner';
import { ProductDetailRatingsAndReviewsSection } from './ratingsAndReviews';

type ProductDetailTemplateProps = PropsWithLocale & {
    product: ProductDetailValue;
    redDotText?: string;
    enablePriceExceptionLegalMentions: boolean;
};

export const PRODUCT_DETAIL_TEMPLATE_ID = 'product-detail-template';
export const DATA_PRODUCT_ID = 'data-product-id';
export const DATA_PRODUCT_SKU = 'data-product-sku';

export const ProductDetailTemplate: AsyncComponent<ProductDetailTemplateProps> = async ({
    product,
    locale,
    redDotText,
    enablePriceExceptionLegalMentions,
}) => {
    const promotionInfosMap = unwrapResultSuccess(
        await getPromotionInfosMapServerAction(product, product.productsSetList),
    );

    const recommendedProductsResult = await getRecommendedProductsServerAction(
        'pdp',
        product.type !== ProductType.PRODUCT_SET ? product.masterId : product.productSetId,
    );
    const recommendedProducts = unwrapResultSuccess(recommendedProductsResult) ?? [];
    const shouldDisplayCrossSellsBundles =
        unwrapResultSuccess(await isCrossSellsBundlesEnabled(locale)) &&
        !product.isBundle &&
        product.type !== ProductType.PRODUCT_SET;

    const brandCornerBanner = product.banners?.find(isBrandCornerBanner) ?? undefined;

    const fontFamily = product.isArialFont ? 'arial' : 'brand';

    const productMasterId = product.type !== ProductType.PRODUCT_SET ? product.masterId : undefined;

    const vipBrandsResult = await getCachedVipBrands(locale);
    const vipBrands = unwrapResultSuccess(vipBrandsResult) ?? {};

    const dataAttributes = {
        [DATA_PRODUCT_ID]: product.type !== ProductType.PRODUCT_SET ? product.masterId : product.productSetId,
        [DATA_PRODUCT_SKU]: product.type !== ProductType.PRODUCT_SET ? product.sku : undefined,
    };

    return (
        <section
            id={PRODUCT_DETAIL_TEMPLATE_ID}
            className={'flex flex-col gap-2 desktop:gap-4'}
            itemScope
            itemType={SchemaOrgItemType.Product}
            {...dataAttributes}
        >
            {productMasterId && <meta itemProp={SchemaOrgItemProp.ProductId} content={productMasterId} />}
            <ProductDetailContainerSection
                promotionInfosMap={promotionInfosMap}
                product={product}
                locale={locale}
                vipBrands={vipBrands}
                fontFamily={fontFamily}
                redDotText={redDotText}
            />
            {recommendedProducts[0] && (
                <ProductDetailCarouselContainer
                    fontFamily={fontFamily}
                    locale={locale}
                    vipBrands={vipBrands}
                    products={recommendedProducts[0].products}
                    title={recommendedProducts[0].title}
                    button={recommendedProducts[0].cta}
                    enablePriceExceptionLegalMentions={enablePriceExceptionLegalMentions}
                />
            )}
            {productMasterId && (
                <ProductDetailRatingsAndReviewsSection
                    rating={product.rating}
                    productId={productMasterId}
                    locale={locale}
                />
            )}

            {shouldDisplayCrossSellsBundles && <CrossSellsBundlesContainer />}
            {recommendedProducts[1] && (
                <ProductDetailCarouselContainer
                    fontFamily={fontFamily}
                    locale={locale}
                    vipBrands={vipBrands}
                    products={recommendedProducts[1].products}
                    title={recommendedProducts[1].title}
                    button={recommendedProducts[1].cta}
                    enablePriceExceptionLegalMentions={enablePriceExceptionLegalMentions}
                />
            )}
            {brandCornerBanner && (
                <ProductDetailBrandCornerSection brandCornerBanner={brandCornerBanner} fontFamily={fontFamily} />
            )}
            <ProductDetailFooterSection
                brandLogoPosition={product.brandLogoPosition}
                brandName={product.brand.name}
                vipFooterLogoUrl={product.vipFooterLogoUrl}
            />
        </section>
    );
};
